import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { soldeActions } from "../../sagas/soldeSlice";
import { Container, Row, Col, Card, CardBody, Button, Form, FormGroup, Label, Input, Table, Spinner } from "reactstrap";
import { FaTruck, FaFileExport, FaSearch, FaFilePdf } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { clientActions } from "../../sagas/clientSlice";
import Select from "react-select";
import { format } from "date-fns";
import "./style.css";
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import { formatPrice } from "../../helpers/utility";
if (typeof window !== "undefined") {
    pdfMake.vfs = window.pdfMake?.vfs || pdfMake.vfs; // make sure vfs is set
}

const MouvementSoldeClient = () => {
    const { soldes, loading, error } = useSelector((state) => state.soldes);
    const clients = useSelector((state) => state.clients.clients);
    const { t } = useTranslation();
    const { company } = useSelector(state => state.auth);

    // Start/end dates
    const startDate = format(new Date(), "yyyy-MM-dd");
    const endDate = format(new Date(Date.now() + 24 * 60 * 60 * 1000), "yyyy-MM-dd");

    const [filters, setFilters] = useState({
        clientId: null,
        dateMouvementFrom: startDate,
        dateMouvementTo: endDate,
        typeDocument: "",
    });

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(clientActions.search({ query: "" }));
        dispatch(soldeActions.search(filters));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = (e) => {
        e && e.preventDefault();
        dispatch(soldeActions.search(filters));
    };

    const handleExport = () => {
        // Your existing export logic if any
    };

    // ---------- Helpers ----------
    const formatMoney = (n) =>
        (Number(n || 0))
            .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const signed = (n) => {
        const x = Number(n || 0);
        if (x > 0) return `+${formatMoney(x)}`;
        if (x < 0) return `-${formatMoney(Math.abs(x))}`;
        return "0.00";
    };

    const clientOptions = useMemo(
        () =>
            clients?.map((client) => ({
                value: client.id,
                label:
                    client.typeClient === "PARTICULIER"
                        ? `${client.nom} ${client.prenom || ""} `
                        : client.entreprise,
            })) ?? [],
        [clients]
    );

    const clientOptionsWithAll = useMemo(
        () => [{ value: null, label: t("common.all", "Tous") }, ...clientOptions],
        [clientOptions, t]
    );

    const selectedClientLabel = useMemo(() => {
        if (!filters.clientId) return t("common.all", "Tous");
        const f = clientOptions.find((c) => String(c.value) === String(filters.clientId));
        return f?.label || t("common.unknown", "Inconnu");
    }, [filters.clientId, clientOptions, t]);

    const totalDebit = soldes?.reduce((sum, m) => sum + (Number(m.debit) || 0), 0) || 0;
    const totalCredit = soldes?.reduce((sum, m) => sum + (Number(m.credit) || 0), 0) || 0;
    const soldeActuel = soldes && soldes.length > 0 ? soldes[soldes.length - 1].soldeApres : 0;
    const soldeAvant = soldes && soldes.length > 0 ? soldes[0].soldeAvant : 0;


    const handleGeneratePdf = () => {
        // expects: t, filters, soldes, selectedClientLabel, company, pdfMake available in scope
        const title = t("nav.customerReports", "Journal des opérations");
        const periodLabel = `${t("common.from", "Du")} ${filters.dateMouvementFrom} ${t("common.to", "au")} ${filters.dateMouvementTo}`;
        const clientLabel = `${t("bonLivraison.client", "Client")} : ${selectedClientLabel}`;

        // Helpers
        const fmt = (n) =>
            formatPrice(n);
        const signed = (n) => {
            const x = Number(n || 0);
            if (x > 0) return `+${fmt(x)}`;
            if (x < 0) return `-${fmt(Math.abs(x))}`;
            return "0.00";
        };
        const joinNonEmpty = (arr, sep) => arr.filter(Boolean).join(sep);

        // Calculations
        const totalDebit = soldes?.reduce((s, m) => s + (Number(m.debit) || 0), 0) || 0;
        const totalCredit = soldes?.reduce((s, m) => s + (Number(m.credit) || 0), 0) || 0;
        const soldeActuel = soldes && soldes.length ? Number(soldes[soldes.length - 1].soldeApres || 0) : 0;

        // Header (only name + subtitle)
        const headerStack = [
            { text: company?.name || "", alignment: "center", bold: true, fontSize: 14, color: "#0F172A", margin: [0, 2, 0, 0] },
            company?.subtitle ? { text: company.subtitle, alignment: "center", fontSize: 11, color: "#475569", margin: [0, 2, 0, 6] } : null,
            { canvas: [{ type: "line", x1: 0, y1: 6, x2: 760, y2: 6, lineWidth: 0.5, color: "#CBD5E1" }] },
            { text: title, alignment: "center", bold: true, fontSize: 12, margin: [0, 10, 0, 0], color: "#0F172A" },
            { text: `${clientLabel}  |  ${periodLabel}`, alignment: "center", fontSize: 9, color: "#475569", margin: [0, 4, 0, 0] },
        ].filter(Boolean);

        // Footer (company info centered)
        const footerAddress = joinNonEmpty([company?.address], " ");
        const footerContacts = joinNonEmpty([company?.phone1, company?.phone2, company?.email], "  |  ");
        const footerLegal = joinNonEmpty([
            company?.identificationFiscale ? `IF: ${company.identificationFiscale}` : null,
            company?.ice ? `ICE: ${company.ice}` : null,
            company?.rc ? `RC: ${company.rc}` : null,
            company?.patente ? `Patente: ${company.patente}` : null,
            company?.cnss ? `CNSS: ${company.cnss}` : null,
        ], "   •   ");
        const footerBank = joinNonEmpty([
            company?.bankName ? `${company.bankName}` : null,
            company?.RIB ? `RIB: ${company.RIB}` : null,
        ], "   •   ");

        // Table rows
        const headerRow = [
            { text: t("common.reference", "Référence"), style: "thead" },
            { text: t("common.client", "Client"), style: "thead" },
            { text: t("common.operation", "Opération"), style: "thead" },
            { text: t("common.operationDate", "Date Opération"), style: "thead" },
            { text: t("common.debit", "Débit"), style: "thead", alignment: "right" },
            { text: t("common.credit", "Crédit"), style: "thead", alignment: "right" },
            { text: t("common.balanceBefore", "Solde Avant"), style: "thead", alignment: "right" },
            { text: t("common.balanceAfter", "Solde Après"), style: "thead", alignment: "right" },
        ];

        const rows = (soldes && soldes.length)
            ? soldes.map((m) => ([
                { text: m.reference || "-", noWrap: true },
                { text: m.client || "-", noWrap: true },
                { text: m.description || "-" },
                { text: m.dateOperation || "-", noWrap: true },
                { text: fmt(m.debit), alignment: "right" },
                { text: fmt(m.credit), alignment: "right" },
                {
                    text: signed(m.soldeAvant),
                    alignment: "right",
                    color: Number(m.soldeAvant) < 0 ? "#dc3545" : Number(m.soldeAvant) > 0 ? "#198754" : "#222",
                },
                {
                    text: signed(m.soldeApres),
                    alignment: "right",
                    color: Number(m.soldeApres) < 0 ? "#dc3545" : Number(m.soldeApres) > 0 ? "#198754" : "#222",
                },
            ]))
            : [[{ text: t("mouvements.noData", "Aucun mouvement trouvé."), colSpan: 8, alignment: "center", italics: true, color: "#6c757d" }, {}, {}, {}, {}, {}, {}, {}]];

        const totalsRow = [
            { text: t("common.totals", "Totaux :"), colSpan: 4, alignment: "right", bold: true }, {}, {}, {},
            { text: fmt(totalDebit), alignment: "right", bold: true },
            { text: fmt(totalCredit), alignment: "right", bold: true },
            { text: `${t("common.balanceAt", "Solde au")} ${filters.dateMouvementTo} :`, alignment: "right", bold: true },
            {
                text: signed(soldeActuel),
                alignment: "right",
                bold: true,
                color: Number(soldeActuel) < 0 ? "#dc3545" : Number(soldeActuel) > 0 ? "#198754" : "#222",
            },
        ];

        // Summary block (below the table): Débit, Crédit, Solde
        const summaryBlock = {
            margin: [0, 10, 0, 0],
            table: {
                widths: ["*", "*", "*"],
                body: [
                    [
                        { text: t("common.totalDebit", "Débit total"), style: "sumLabel", alignment: "center" },
                        { text: t("common.totalCredit", "Crédit total"), style: "sumLabel", alignment: "center" },
                        { text: t("common.balanceAfter", "Solde actuel"), style: "sumLabel", alignment: "center" },
                    ],
                    [
                        { text: fmt(totalDebit), style: "sumValue", alignment: "center" },
                        { text: fmt(totalCredit), style: "sumValue", alignment: "center" },
                        {
                            text: signed(soldeActuel),
                            style: "sumValue",
                            alignment: "center",
                            color: Number(soldeActuel) < 0 ? "#dc3545" : Number(soldeActuel) > 0 ? "#198754" : "#0F172A",
                        },
                    ],
                ],
            },
            layout: {
                fillColor: (rowIndex) => (rowIndex === 0 ? "#E0F2FE" : "#F0F9FF"), // soft blue highlight
                hLineColor: () => "#38BDF8",
                vLineColor: () => "#38BDF8",
                hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 0.8 : 0.5),
                vLineWidth: () => 0.5,
                paddingLeft: () => 8,
                paddingRight: () => 8,
                paddingTop: () => 8,
                paddingBottom: () => 8,
            },
        };

        // Document
        const docDefinition = {
            pageOrientation: "landscape",
            pageSize: "A4",
            pageMargins: [40, 100, 40, 90],
            header: { margin: [40, 20, 40, 0], stack: headerStack },
            content: [
                {
                    table: {
                        headerRows: 1,
                        widths: [80, 100, "*", 90, 70, 70, 80, 80],
                        body: [headerRow, ...rows, totalsRow],
                    },
                    layout: {
                        fillColor: (i) => (i === 0 ? "#E9F2FF" : i % 2 === 0 ? "#FFFFFF" : "#F8FAFC"),
                        hLineColor: () => "#E2E8F0",
                        vLineColor: () => "#E2E8F0",
                    },
                },
                summaryBlock, // ✅ placed below the table
            ],
            styles: {
                thead: { bold: true, fontSize: 10, color: "#0F172A" },
                sumLabel: { fontSize: 10, bold: true, color: "#0F172A" },
                sumValue: { fontSize: 12, bold: true, color: "#0F172A" },
            },
            defaultStyle: { fontSize: 9, color: "#0F172A" },
            footer: (currentPage, pageCount) => ({
                margin: [40, 10, 40, 16],
                stack: [
                    footerAddress ? { text: footerAddress, alignment: "center", fontSize: 9, color: "#475569" } : null,
                    footerContacts ? { text: footerContacts, alignment: "center", fontSize: 9, color: "#475569", margin: [0, 2, 0, 0] } : null,
                    footerLegal ? { text: footerLegal, alignment: "center", fontSize: 9, color: "#64748B", margin: [0, 2, 0, 0] } : null,
                    footerBank ? { text: footerBank, alignment: "center", fontSize: 9, color: "#64748B", margin: [0, 2, 0, 6] } : null,
                    {
                        text: `${t("common.generatedOn", "Généré le")} ${format(new Date(), "yyyy-MM-dd HH:mm")}  •  ${currentPage}/${pageCount}`,
                        alignment: "center",
                        fontSize: 8,
                        color: "#94A3B8",
                    },
                ].filter(Boolean),
            }),
        };

        pdfMake.createPdf(docDefinition).open(); // or .download('journal-operations.pdf')
    };





    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaTruck className="text-primary me-2" />
                                {t("nav.customerReports", "Journal des opérations")}
                            </h4>
                            <div className="d-flex align-items-center">
                                <Button color="danger" onClick={handleGeneratePdf} className="me-2">
                                    <FaFilePdf className="me-2" />
                                    {t("common.pdf", "PDF")}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Search Filters */}
            <Row className="mb-3">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody>
                            <Form inline onSubmit={handleSearch} className="row g-3 ">
                                <Col md={3}>
                                    <Label htmlFor="clientId">{t("bonLivraison.client")}:</Label>
                                    <Select
                                        id="clientId"
                                        options={clientOptionsWithAll}
                                        value={clientOptionsWithAll.find((option) => option.value === filters.clientId)}
                                        onChange={(selected) =>
                                            setFilters((f) => ({ ...f, clientId: selected ? selected.value : null }))
                                        }
                                        placeholder={t("bonLivraison.selectClient")}
                                        isSearchable
                                        menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Col>
                                <Col md={3}>
                                    <FormGroup>
                                        <Label for="dateMouvementFrom">{t("common.startDate", "Date début")}</Label>
                                        <Input
                                            type="date"
                                            id="dateMouvementFrom"
                                            value={filters.dateMouvementFrom}
                                            onChange={(e) => setFilters((f) => ({ ...f, dateMouvementFrom: e.target.value }))}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup>
                                        <Label for="dateMouvementTo">{t("common.endDate", "Date fin")}</Label>
                                        <Input
                                            type="date"
                                            id="dateMouvementTo"
                                            value={filters.dateMouvementTo}
                                            onChange={(e) => setFilters((f) => ({ ...f, dateMouvementTo: e.target.value }))}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={2}>
                                    <Button color="primary" type="submit" className="mt-4">
                                        <FaSearch className="me-2" />
                                        {t("search", "Rechercher")}
                                    </Button>
                                </Col>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Table of mouvements */}
            <Row>
                <Col>
                    <Card className="shadow-sm">
                        <CardBody>
                            {loading ? (
                                <div className="text-center my-4">
                                    <Spinner color="primary" />
                                </div>
                            ) : error ? (
                                <div className="text-danger">{t("common.errorLoading", "Erreur lors du chargement des mouvements.")}</div>
                            ) : (
                                <Table bordered hover size="sm" className="mb-0 align-middle">
                                    <thead className="table-primary">
                                        <tr>
                                            <th>Référence</th>
                                            <th>Client</th>
                                            <th>Opération</th>
                                            <th>Date Opération</th>
                                            <th>Débit</th>
                                            <th>Crédit</th>
                                            <th>Solde Avant</th>
                                            <th>Solde Après</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {soldes && soldes.length > 1 ? (
                                            soldes.map((m, idx) => (
                                                <tr key={idx} className={idx === 0 ? "fw-bold" : ""}>
                                                    <td>{m.reference}</td>
                                                    <td>{m.client}</td>
                                                    <td className="wrap-text">{m.description}</td>
                                                    <td>{m.dateOperation}</td>
                                                    <td>{formatMoney(m.debit)}</td>
                                                    <td>{formatMoney(m.credit)}</td>
                                                    <td>
                                                        {Number(m.soldeAvant) > 0 ? (
                                                            <b>
                                                                <span className="text-success">+{formatMoney(Math.abs(Number(m.soldeAvant)))}</span>
                                                            </b>
                                                        ) : Number(m.soldeAvant) < 0 ? (
                                                            <b>
                                                                <span className="text-danger">-{formatMoney(Math.abs(Number(m.soldeAvant)))}</span>
                                                            </b>
                                                        ) : (
                                                            <b>
                                                                <span className="text-dark">0.00</span>
                                                            </b>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {Number(m.soldeApres) > 0 ? (
                                                            <b>
                                                                <span className="text-success">+{formatMoney(Math.abs(Number(m.soldeApres)))}</span>
                                                            </b>
                                                        ) : Number(m.soldeApres) < 0 ? (
                                                            <b>
                                                                <span className="text-danger">-{formatMoney(Math.abs(Number(m.soldeApres)))}</span>
                                                            </b>
                                                        ) : (
                                                            <b>
                                                                <span className="text-dark">0.00</span>
                                                            </b>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={9} className="text-center py-5">
                                                    <div className="d-flex flex-column align-items-center justify-content-center">
                                                        <span style={{ fontSize: "2rem", color: "#adb5bd" }}>
                                                            <FaTruck />
                                                        </span>
                                                        <div className="mt-2 text-muted">
                                                            {t("mouvements.noData", "Aucun mouvement trouvé.")}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    {soldes && soldes.length > 1 ? (
                                        <tfoot className="table-light">
                                            <tr className="table-secondary fw-bold">
                                                <td colSpan={4} className="text-end">
                                                    Totaux :
                                                </td>
                                                <td>{formatMoney(totalDebit)}</td>
                                                <td>{formatMoney(totalCredit)}</td>
                                                <td colSpan={2} className="text-start">
                                                    {`Solde au ${filters.dateMouvementTo} : `}
                                                    {Number(soldeActuel) > 0 ? (
                                                        <b>
                                                            <span className="text-success">+{formatMoney(Math.abs(Number(soldeActuel)))}</span>
                                                        </b>
                                                    ) : Number(soldeActuel) < 0 ? (
                                                        <b>
                                                            <span className="text-danger">-{formatMoney(Math.abs(Number(soldeActuel)))}</span>
                                                        </b>
                                                    ) : (
                                                        <b>
                                                            <span className="text-dark">0.00</span>
                                                        </b>
                                                    )}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    ) : null}
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default MouvementSoldeClient;
